// src/oura/components/search/TrafficMarkers.tsx
//
// What happened when, under the traffic chart: work Oura published, and
// outside events staff marked so a spike is not credited to the wrong thing
// (ourteam-backend#6426). The weekly averages either side of the newest marker
// are figures only; the card never says the marker caused the change.

import { useState } from "react";
import { FlagIcon, PlusIcon, XIcon } from "lucide-react";
import type { AeoTrafficMarker } from "@service/aeo";
import { addTrafficMarker, MARKER_LABEL_MAX, removeTrafficMarker } from "@service/aeo/trafficMarkers";
import { PillButton, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { shortDate } from "../../lib/oura";
import { weekIndexOf } from "./format";

/** Weeks either side of a marker that the before-and-after average covers. */
const AROUND_WEEKS = 4;

const avg = (xs: number[]) => Math.round(xs.reduce((a, b) => a + b, 0) / xs.length);

export function TrafficMarkers({
    markers,
    weeks,
    businessId,
    isStaff,
    onChanged,
}: {
    markers: AeoTrafficMarker[];
    /** The weekly series on the chart, oldest first. */
    weeks: { start?: string; sessions: number }[];
    businessId?: string;
    isStaff?: boolean;
    /** Reload the payload after a marker was added or removed. */
    onChanged?: () => void | Promise<void>;
}) {
    const [adding, setAdding] = useState(false);
    const [date, setDate] = useState("");
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const first = weeks[0]?.start;
    // Oldest first whatever order the API sends.
    const shown = markers.filter((m) => !first || m.date >= first).sort((a, b) => a.date.localeCompare(b.date));
    // Before and after for the newest marker with a full baseline before it and 2 weeks after.
    const newest = [...shown].reverse().find((m) => {
        const i = weekIndexOf(weeks, m.date);
        // Two whole weeks after the marker's own week.
        return i >= AROUND_WEEKS && i + 2 < weeks.length;
    });
    const at = newest ? weekIndexOf(weeks, newest.date) : -1;
    const before = at > 0 ? weeks.slice(Math.max(0, at - AROUND_WEEKS), at).map((w) => w.sessions) : [];
    const after = at > 0 ? weeks.slice(at, at + AROUND_WEEKS).map((w) => w.sessions) : [];

    const canEdit = !!isStaff && !!businessId;
    if (shown.length === 0 && !canEdit) return null;

    async function save() {
        if (!businessId) return;
        setBusy(true);
        setError(null);
        const res = await addTrafficMarker(businessId, date, label.trim());
        setBusy(false);
        if (!res.ok) return setError(res.message);
        setAdding(false);
        setDate("");
        setLabel("");
        await onChanged?.();
    }

    async function remove(id: string) {
        if (!businessId) return;
        setBusy(true);
        setError(null);
        const res = await removeTrafficMarker(businessId, id);
        setBusy(false);
        if (!res.ok) return setError(res.message);
        await onChanged?.();
    }

    return (
        <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between gap-3">
                <span className={cn(TYPE.tc, "font-semibold text-gray-700")}>{isStaff ? "Markers" : "What happened when"}</span>
                {canEdit && !adding && (
                    <PillButton size="sm" icon={PlusIcon} onClick={() => setAdding(true)}>Add marker</PillButton>
                )}
            </div>
            {newest && before.length > 0 && after.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-gray-50 px-3.5 py-2.5">
                        <div className={cn(TYPE.tc, "text-gray-500")}>Weekly average, {before.length} weeks before {shortDate(newest.date)}</div>
                        <div className="text-lg font-semibold tabular-nums text-gray-900">{avg(before).toLocaleString("en-GB")}</div>
                    </div>
                    <div className="rounded-xl bg-blue-50 px-3.5 py-2.5">
                        <div className={cn(TYPE.tc, "text-blue-700")}>Weekly average since</div>
                        <div className="text-lg font-semibold tabular-nums text-gray-900">{avg(after).toLocaleString("en-GB")}</div>
                    </div>
                </div>
            )}
            {shown.map((m) => (
                <div key={`${m.kind}-${m.id ?? m.date}-${m.label}`} className={cn(TYPE.tb, "flex items-center gap-2")}>
                    <FlagIcon aria-hidden size={13} className={m.kind === "work" ? "text-blue-600" : "text-amber-600"} />
                    <span className="w-24 shrink-0 whitespace-nowrap tabular-nums text-gray-500">{shortDate(m.date)}</span>
                    <span className="min-w-0 flex-1 text-gray-900">{m.label}</span>
                    {canEdit && m.kind === "staff" && m.id && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => remove(m.id!)}
                            aria-label={`Remove marker ${m.label}`}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                        >
                            <XIcon size={14} />
                        </button>
                    )}
                </div>
            ))}
            {canEdit && adding && (
                <form
                    className="flex flex-wrap items-center gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void save();
                    }}
                >
                    <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className={cn(TYPE.tb, "rounded-lg border border-gray-200 px-2.5 py-1.5")} />
                    <input
                        required
                        maxLength={MARKER_LABEL_MAX}
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="A sale, an ad campaign, a TV spot"
                        className={cn(TYPE.tb, "min-w-0 flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5")}
                    />
                    <PillButton size="sm" type="submit" disabled={busy || !date || !label.trim()}>Save</PillButton>
                    <PillButton size="sm" onClick={() => setAdding(false)}>Cancel</PillButton>
                </form>
            )}
            {error && <p className={cn(TYPE.tc, "m-0 text-rose-700")}>{error}</p>}
            {isStaff && (
                <p className={cn(TYPE.tm, "m-0 text-gray-400")}>Mark outside events so a rise is not credited to the wrong thing. Clients see these markers.</p>
            )}
        </div>
    );
}
